import { log } from '../utils/logger';

/**
 * Authentication API module
 * Handles all authentication-related API operations
 */

import { supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Authentication API namespace
 */
export const authAPI = {
  /**
   * Sign up a new user
   * @param {Object} data - User registration data
   * @returns {Promise<APIResponse>}
   */
  signUp: createAPIMethod(
    async (data) => {
      const { email, password, ...userData } = data;

      // Use current origin for proper deep linking on email confirmation
      const redirectUrl = `${window.location.origin}/auth/confirm`;

      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: redirectUrl
        }
      });

      if (error) throw error;

      return authData;
    },
    { 
      operation: 'Sign up user',
      transformData: (data) => {
        log.info('👤 New user signed up:', data?.user?.email);
        return data;
      }
    }
  ),

  /**
   * Sign in a user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<APIResponse>}
   */
  signIn: createAPIMethod(
    async ({ email, password }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      return data;
    },
    { 
      operation: 'Sign in user',
      transformData: (data) => {
        log.info('🔐 User signed in:', data?.user?.email);
        return data;
      }
    }
  ),

  /**
   * Sign out current user
   * @returns {Promise<APIResponse>}
   */
  signOut: createAPIMethod(
    async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    },
    { operation: 'Sign out user' }
  ),

  /**
   * Get current session
   * @returns {Promise<APIResponse>}
   */
  getSession: createAPIMethod(
    async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data;
    },
    { operation: 'Get current session' }
  ),

  /**
   * Get current user
   * @returns {Promise<APIResponse>}
   */
  getCurrentUser: createAPIMethod(
    async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data;
    },
    { operation: 'Get current user' }
  ),

  /**
   * Update user profile
   * @param {Object} updates - Profile updates
   * @returns {Promise<APIResponse>}
   */
  updateProfile: createAPIMethod(
    async (updates) => {
      const { data, error } = await supabase.auth.updateUser(updates);
      if (error) throw error;
      return data;
    },
    { operation: 'Update user profile' }
  ),

  /**
   * Reset password
   * @param {string} email - User email
   * @returns {Promise<APIResponse>}
   */
  resetPassword: createAPIMethod(
    async (email) => {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Reset password' }
  ),

  /**
   * Update password
   * @param {string} newPassword - New password
   * @returns {Promise<APIResponse>}
   */
  updatePassword: createAPIMethod(
    async (newPassword) => {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Update password' }
  ),

  /**
   * Refresh session
   * @returns {Promise<APIResponse>}
   */
  refreshSession: createAPIMethod(
    async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return data;
    },
    { operation: 'Refresh session' }
  ),

  /**
   * Check if user exists
   * @param {string} email - Email to check
   * @returns {Promise<APIResponse>}
   */
  checkUserExists: createAPIMethod(
    async (email) => {
      const { data, error } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found

      return {
        exists: !!data,
        user: data || null
      };
    },
    { operation: 'Check if user exists' }
  ),

  /**
   * Get user profile data
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getUserProfile: createAPIMethod(
    async (userId) => {
      return await supabase
        .from('users')
        .select(`
          *,
          managed_teams:teams!manager_id (
            id, name, division, sport_activity
          ),
          organized_tournaments:tournaments!organizer_id (
            id, name, start_date, end_date
          )
        `)
        .eq('id', userId)
        .single();
    },
    { operation: 'Get user profile' }
  ),

  /**
   * Update user profile data in database
   * @param {string} userId - User ID
   * @param {Object} data - Profile data to update
   * @returns {Promise<APIResponse>}
   */
  updateUserProfile: createAPIMethod(
    async (userId, data) => {
      return await supabase
        .from('users')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();
    },
    { operation: 'Update user profile data' }
  ),

  /**
   * Delete user account
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  deleteUser: createAPIMethod(
    async (userId) => {
      // Note: This only marks the user as deleted in the database
      // The actual auth user deletion would need to be handled server-side
      return await supabase
        .from('users')
        .update({
          is_active: false,
          deleted_at: new Date().toISOString()
        })
        .eq('id', userId);
    },
    { operation: 'Delete user account' }
  ),

  /**
   * Request team manager access
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID requesting access
   * @param {string} reason - Reason for request
   * @returns {Promise<APIResponse>}
   */
  requestTeamAccess: createAPIMethod(
    async (teamId, userId, reason = '') => {
      // Check if request already exists
      const { data: existing } = await supabase
        .from('pending_team_managers')
        .select('id')
        .eq('team_id', teamId)
        .eq('user_id', userId)
        .eq('status', 'pending')
        .single();

      if (existing) {
        throw new Error('Access request already pending for this team');
      }

      return await supabase
        .from('pending_team_managers')
        .insert({
          team_id: teamId,
          user_id: userId,
          justification: reason,
          status: 'pending',
          requested_at: new Date().toISOString()
        })
        .select(`
          *,
          teams (
            id, name
          )
        `)
        .single();
    },
    { operation: 'Request team manager access' }
  ),

  /**
   * Approve team manager access request
   * @param {string} requestId - Request ID
   * @param {string} approverId - User ID of approver
   * @returns {Promise<APIResponse>}
   */
  approveTeamAccess: createAPIMethod(
    async (requestId, approverId) => {
      // Get the request details
      const { data: request } = await supabase
        .from('pending_team_managers')
        .select('team_id, user_id')
        .eq('id', requestId)
        .single();

      if (!request) {
        throw new Error('Access request not found');
      }

      // Update team manager
      await supabase
        .from('teams')
        .update({
          manager_id: request.user_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.team_id);

      // Update request status
      return await supabase
        .from('pending_team_managers')
        .update({
          status: 'approved',
          approved_by: approverId,
          approved_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .select()
        .single();
    },
    { operation: 'Approve team access request' }
  ),

  /**
   * Deny team manager access request
   * @param {string} requestId - Request ID
   * @param {string} approverId - User ID of approver
   * @param {string} reason - Reason for denial
   * @returns {Promise<APIResponse>}
   */
  denyTeamAccess: createAPIMethod(
    async (requestId, approverId, reason = '') => {
      return await supabase
        .from('pending_team_managers')
        .update({
          status: 'denied',
          denied_by: approverId,
          denied_at: new Date().toISOString(),
          denial_reason: reason
        })
        .eq('id', requestId)
        .select()
        .single();
    },
    { operation: 'Deny team access request' }
  ),

  /**
   * Get user's access requests
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getUserAccessRequests: createAPIMethod(
    async (userId) => {
      return await supabase
        .from('pending_team_managers')
        .select(`
          *,
          teams (
            id, name, division, sport_activity
          )
        `)
        .eq('user_id', userId)
        .order('requested_at', { ascending: false });
    },
    { operation: 'Get user access requests' }
  ),

  /**
   * Set up auth state change listener
   * @param {Function} callback - Callback function for auth state changes
   * @returns {Object} Subscription object
   */
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  /**
   * Check if user has specific role
   * @param {string} userId - User ID
   * @param {string} role - Role to check
   * @returns {Promise<APIResponse>}
   */
  hasRole: createAPIMethod(
    async (userId, role) => {
      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      return {
        hasRole: data?.role === role,
        currentRole: data?.role || null
      };
    },
    { operation: 'Check user role' }
  ),

  /**
   * Verify email
   * @param {string} token - Verification token
   * @returns {Promise<APIResponse>}
   */
  verifyEmail: createAPIMethod(
    async (token) => {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email'
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Verify email' }
  ),

  /**
   * Resend email verification
   * @param {string} email - User email
   * @returns {Promise<APIResponse>}
   */
  resendVerification: createAPIMethod(
    async (email) => {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Resend email verification' }
  )
};